import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Check, X, Clock, AlertCircle, Tag, User, Shield, Store } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { api } from '../services/api';
import { useAuth } from '../context/AuthContext';
import AuthModal from '../components/AuthModal';
import { toast } from 'sonner';

const STATUS_CONFIG = {
  reservado: { label: 'Ativo', color: 'bg-amber-100 text-amber-700 border-amber-300', icon: Clock, message: 'Pronto para usar' },
  utilizado: { label: 'Utilizado', color: 'bg-green-100 text-green-700 border-green-300', icon: Check, message: 'Este voucher já foi validado' },
  expirado:  { label: 'Expirado', color: 'bg-gray-100 text-gray-500 border-gray-300', icon: X, message: 'Este voucher expirou' },
  cancelado: { label: 'Cancelado', color: 'bg-red-100 text-red-600 border-red-300', icon: X, message: 'Este voucher foi cancelado' },
};

export default function ValidarVoucherPage() {
  const { code } = useParams();
  const navigate = useNavigate();
  const { user, token, isAuthenticated } = useAuth();
  const [voucher, setVoucher] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pin, setPin] = useState('');
  const [validating, setValidating] = useState(false);
  const [validated, setValidated] = useState(false);
  const [showAuth, setShowAuth] = useState(false);

  useEffect(() => {
    if (!code) return;
    setLoading(true);
    api.get(`/v2/vouchers/lookup/${code.toUpperCase()}`)
      .then(res => setVoucher(res.data))
      .catch(() => setError('Voucher não encontrado'))
      .finally(() => setLoading(false));
  }, [code]);

  const handleValidate = async () => {
    if (!isAuthenticated) {
      setShowAuth(true);
      return;
    }
    if (!pin || pin.length !== 4) {
      toast.error('Digite o PIN de 4 dígitos');
      return;
    }
    setValidating(true);
    try {
      const res = await api.post('/v2/vouchers/lojista/validate', {
        voucher_code: code.toUpperCase(),
        pin,
        business_id: voucher.business_id,
      });
      setValidated(true);
      setVoucher(res.data.voucher);
      toast.success('Cupom validado com sucesso!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro na validação');
    } finally {
      setValidating(false);
      setPin('');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="w-8 h-8 border-3 border-purple-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error || !voucher) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <Card className="max-w-md w-full text-center py-12">
          <CardContent className="space-y-4">
            <AlertCircle className="w-12 h-12 text-red-400 mx-auto" />
            <h2 className="text-xl font-bold">Voucher não encontrado</h2>
            <p className="text-sm text-gray-500">O código <strong>{code}</strong> não corresponde a nenhum voucher válido.</p>
            <Button variant="outline" onClick={() => navigate('/')}>Voltar ao início</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const status = STATUS_CONFIG[voucher.status] || STATUS_CONFIG.reservado;
  const StatusIcon = status.icon;
  const discount = voucher.tipo_desconto === 'percentual' ? `${voucher.coupon_desconto}%` : `R$ ${voucher.coupon_desconto}`;

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white flex items-start justify-center px-4 py-8 sm:py-16" data-testid="validar-voucher-page">
      <div className="max-w-md w-full space-y-4">
        {/* Voucher details */}
        <Card className={`border-2 ${voucher.status === 'reservado' ? 'border-amber-300' : voucher.status === 'utilizado' ? 'border-green-300' : 'border-gray-200'}`} data-testid="voucher-card">
          <CardContent className="p-5 space-y-4">
            {/* Status banner */}
            <div className={`flex items-center gap-2 px-3 py-2 rounded-lg ${status.color}`}>
              <StatusIcon className="w-4 h-4" />
              <span className="text-sm font-semibold">{status.label}</span>
              <span className="text-xs ml-auto">{status.message}</span>
            </div>

            {/* Discount */}
            <div className="text-center py-3">
              <p className="text-4xl font-black text-amber-600">{discount} OFF</p>
              <h1 className="text-lg font-bold text-gray-900 mt-1">{voucher.coupon_titulo}</h1>
            </div>

            {/* Details */}
            <div className="space-y-2 bg-gray-50 rounded-lg p-3">
              <div className="flex items-center gap-2 text-sm">
                <Tag className="w-4 h-4 text-gray-400" />
                <span className="text-gray-600">Voucher:</span>
                <span className="font-mono font-bold">{voucher.voucher_code}</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <Store className="w-4 h-4 text-gray-400" />
                <span className="text-gray-600">Negócio:</span>
                <span className="font-medium">{voucher.business_name}</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <User className="w-4 h-4 text-gray-400" />
                <span className="text-gray-600">Cliente:</span>
                <span className="font-medium">{voucher.user_name || voucher.user_email}</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <Clock className="w-4 h-4 text-gray-400" />
                <span className="text-gray-600">Expira:</span>
                <span>{new Date(voucher.expires_at).toLocaleDateString('pt-BR')} às {new Date(voucher.expires_at).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}</span>
              </div>
            </div>

            {/* Validation section */}
            {validated ? (
              <div className="text-center py-4 space-y-3" data-testid="validation-success">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                  <Check className="w-8 h-8 text-green-600" />
                </div>
                <h3 className="text-lg font-bold text-green-700">Validado com sucesso!</h3>
                <p className="text-sm text-gray-500">O desconto de {discount} foi confirmado.</p>
              </div>
            ) : voucher.status === 'reservado' ? (
              <div className="space-y-3 border-t pt-4" data-testid="validation-form">
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <Shield className="w-4 h-4 text-[#56007F]" />
                  <span className="font-medium">Validação do lojista</span>
                </div>
                <p className="text-xs text-gray-500">Digite seu PIN de 4 dígitos para confirmar o uso deste cupom.</p>
                <Input
                  type="password"
                  inputMode="numeric"
                  maxLength={4}
                  placeholder="PIN"
                  value={pin}
                  onChange={e => setPin(e.target.value.replace(/\D/g, '').slice(0, 4))}
                  className="text-center text-2xl tracking-[0.5em] max-w-[180px] mx-auto"
                  onKeyDown={e => e.key === 'Enter' && handleValidate()}
                  data-testid="validate-pin-input"
                />
                <Button
                  onClick={handleValidate}
                  disabled={validating || pin.length !== 4}
                  className="w-full bg-green-600 hover:bg-green-700"
                  data-testid="validate-confirm-btn"
                >
                  {validating ? (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <span className="flex items-center gap-2"><Check className="w-4 h-4" /> Confirmar uso do cupom</span>
                  )}
                </Button>
              </div>
            ) : (
              <div className="text-center py-2">
                <p className="text-sm text-gray-500">{status.message}</p>
              </div>
            )}
          </CardContent>
        </Card>

        <Button variant="outline" className="w-full" onClick={() => navigate('/')}>
          Voltar ao início
        </Button>
      </div>

      <AuthModal isOpen={showAuth} onClose={() => setShowAuth(false)} />
    </div>
  );
}
